import { create } from 'zustand';
import {
  getProjects,
  getSubmissions,
  createProject as createProjectService,
  updateProject as updateProjectService,
  Project,
  Submission,
} from '@/services/TrainerServices/GroupProjectManagementService';

interface GroupProjectManagementState {
  projects: Project[] | null;
  submissions: Submission[] | null;
  loading: boolean;
  error: string | null;

  fetchProjects: () => Promise<void>;
  fetchSubmissions: (projectId: number, filters?: { teamId?: number; status?: string }) => Promise<void>;
  createProject: (projectData: {
    name: string;
    description?: string;
    status?: 'draft' | 'active' | 'completed' | 'archived';
    maxTeams: number;
    membersPerTeam: number;
    deadline?: string;
    startDate?: string;
    instructions?: string;
    gradingCriteria?: string;
    totalPoints?: number;
  }) => Promise<{ success: boolean; data?: Project; error?: { message: string } }>;
  updateProject: (projectId: number, projectData: Partial<Project>) => Promise<{ success: boolean; data?: Project; error?: { message: string } }>;
}

const useGroupProjectManagementStore = create<GroupProjectManagementState>((set, get) => ({
  projects: null,
  submissions: null,
  loading: false,
  error: null,

  fetchProjects: async () => {
    try {
      set({ loading: true, error: null });
      const response = await getProjects();
      set({ projects: response.data || [], loading: false });
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  fetchSubmissions: async (projectId: number, filters?: { teamId?: number; status?: string }) => {
    try {
      set({ loading: true, error: null });
      const response = await getSubmissions(projectId, filters);
      set({ submissions: response.data || [], loading: false });
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  createProject: async (projectData) => {
    try {
      set({ loading: true, error: null });
      const response = await createProjectService(projectData);

      if (response.success && response.data) {
        // Add the new project to the projects list
        const currentProjects = get().projects || [];
        set({ projects: [response.data, ...currentProjects], loading: false });
      } else {
        set({ loading: false, error: response.error?.message || 'Failed to create project' });
      }

      return response;
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to create project';
      set({ error: errorMessage, loading: false });
      return { success: false, error: { message: errorMessage } };
    }
  },

  updateProject: async (projectId, projectData) => {
    try {
      set({ loading: true, error: null });
      const response = await updateProjectService(projectId, projectData);

      if (response.success && response.data) {
        // Update the project in the projects list
        const currentProjects = get().projects || [];
        const updatedProjects = currentProjects.map(p =>
          p.id === projectId ? response.data! : p
        );
        set({ projects: updatedProjects, loading: false });
      } else {
        set({ loading: false, error: response.error?.message || 'Failed to update project' });
      }

      return response;
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to update project';
      set({ error: errorMessage, loading: false });
      return { success: false, error: { message: errorMessage } };
    }
  },
}));

export default useGroupProjectManagementStore;
